// ============================================================
// vinylCalc.js
// Vinyl (PVC) Privacy estimator engine.
//
// Mirrors src/utils/chainLinkCalc.js's structure and output contract so it is a
// drop-in for the same Estimator UI → NewDocument/EditDocument flow:
//   - export function calculateVinyl(specs, products, priceLevel = 'A')
//   - returns exactly { items, warnings }
//   - identical lookupSKU / getPrice / addItem semantics and line-item shape
//   - final SKU-merge pass (same-sku lines summed, sku-less lines appended last)
//
// Prices are NOT here — line prices are resolved by SKU against the Price List
// (Firestore `products`) at the given priceLevel (A/B/C).
//
// Product: PVC Full Privacy fence — one style, 6' tall, 8'-wide panels.
//
// Rules (implemented in this section order):
//   1) Panels      — per run: ceil(length / 8); summed across runs.
//   2) Line posts  — per run: length < 8 → 0, else ceil(length / 8) - 1; summed.
//   3) Corner posts— totalCorners (user-entered).
//   4) End posts   — totalEnds (user-entered; INCLUDES gate posts).
//   5) Post caps   — 1 per post (line + corner + end); style by capType (dflt horse).
//   6) Gates       — per gate: leaves = double?2:1.
//                    1 gate panel/leaf + 1 stiffener/leaf + 1 hinge set/leaf,
//                    1 latch per gate (single OR double pair; type set job-wide,
//                    default paddle), and 1 drop rod per DOUBLE pair only.
//
// Bad input is tolerated: runs/gates default to [], numeric fields to 0,
// non-positive run lengths are ignored, and a gate with an unknown width (not
// '4'/'5') resolves to an undefined panel SKU that surfaces as PRODUCT NOT FOUND.
// ============================================================

import {
  VINYL_PANEL_SKU, VINYL_LINE_POST_SKU, VINYL_CORNER_POST_SKU, VINYL_END_POST_SKU,
  VINYL_CAP_SKU, VINYL_GATE_PANEL_SKU, VINYL_GATE_STIFFENER_SKU, VINYL_GATE_HINGE_SKU,
  VINYL_LATCH_SKU, VINYL_DROP_ROD_SKU,
} from '../data/vinylSKUs';

const lookupSKU = (sku, products) => products.find(p => p.sku === sku && p.isActive);

export function calculateVinyl(specs, products, priceLevel = 'A') {
  const {
    runs = [],
    totalCorners = 0,
    totalEnds = 0,
    capType = 'horse',
    latchType = 'paddle',
    gates = [],
  } = specs || {};

  const warnings = [];
  const items = [];

  const getPrice = (product) => {
    if (!product) return 0;
    if (priceLevel === 'B') return parseFloat(product.priceB || 0);
    if (priceLevel === 'C') return parseFloat(product.priceC || 0);
    return parseFloat(product.priceA || 0);
  };

  // Mirrors chainLinkCalc's addItem EXCEPT the guard: chainLink no-ops on a falsy
  // sku (`if (!sku || qty <= 0) return`); here we guard only qty so an
  // undefined/missing SKU still surfaces as a "PRODUCT NOT FOUND" warning line —
  // e.g. a gate whose width isn't '4'/'5', whose panel SKU resolves to undefined.
  const addItem = (sku, qty, fallbackLabel, unit = 'pc', roundable = false) => {
    if (qty <= 0) return;
    const product = lookupSKU(sku, products);
    if (!product) warnings.push(sku);
    items.push({
      id: Date.now() + Math.random(),
      sku,
      description: product ? product.description : `⚠️ PRODUCT NOT FOUND - SKU: ${sku}`,
      qty: Math.ceil(qty),
      unit: product?.unit || unit,
      unitPrice: getPrice(product),
      isStock: product?.isStock ?? true,
      productId: product?.id || null,
      found: !!product,
      roundable,
      fromEstimator: true,
      estimatorKind: 'vinyl',
    });
  };

  const safeRuns = Array.isArray(runs) ? runs : [];
  const safeGates = Array.isArray(gates) ? gates : [];
  const corners = parseInt(totalCorners) || 0;
  const ends = parseInt(totalEnds) || 0;

  // 1) Panels — per run: ceil(length / 8), summed.
  let totalPanels = 0;
  safeRuns.forEach(r => {
    const len = parseFloat(r?.length) || 0;
    if (len <= 0) return;
    totalPanels += Math.ceil(len / 8);
  });
  addItem(VINYL_PANEL_SKU, totalPanels, 'Vinyl Privacy Panel 6x8');

  // 2) Line posts — per run: length < 8 → 0, else ceil(length / 8) - 1, summed.
  let totalLinePosts = 0;
  safeRuns.forEach(r => {
    const len = parseFloat(r?.length) || 0;
    if (len < 8) return; // includes non-positive lengths → 0 line posts
    totalLinePosts += Math.ceil(len / 8) - 1;
  });
  addItem(VINYL_LINE_POST_SKU, totalLinePosts, 'Vinyl Line Post');

  // 3) Corner posts
  addItem(VINYL_CORNER_POST_SKU, corners, 'Vinyl Corner Post');

  // 4) End posts (user-entered total already includes gate posts)
  addItem(VINYL_END_POST_SKU, ends, 'Vinyl End Post');

  // 5) Post caps — 1 per post; style user-selectable (default Horse).
  const totalPosts = totalLinePosts + corners + ends;
  const capSku = VINYL_CAP_SKU[capType || 'horse'];
  addItem(capSku, totalPosts, 'Vinyl Post Cap');

  // 6) Gates — prebuilt panel(s) + hardware per gate. addItem merges by SKU at the
  // end, so multiple gates of the same width/type sum correctly.
  const latchSku = VINYL_LATCH_SKU[latchType || 'paddle'];
  safeGates.forEach(gate => {
    const width = gate?.width;
    const kind = gate?.kind;
    const leaves = kind === 'double' ? 2 : 1;

    const gatePanelSku = VINYL_GATE_PANEL_SKU[width]; // undefined for unknown width → PRODUCT NOT FOUND
    addItem(gatePanelSku, leaves, 'Vinyl Gate Panel');            // 1 per leaf
    addItem(VINYL_GATE_STIFFENER_SKU, leaves, 'Aluminum Stiffener'); // 1 per leaf
    addItem(VINYL_GATE_HINGE_SKU, leaves, 'Gate Hinge Set');         // 1 per leaf
    addItem(latchSku, 1, 'Gate Latch');                              // 1 per gate (single OR double pair)
    if (kind === 'double') addItem(VINYL_DROP_ROD_SKU, 1, 'Drop Rod'); // 1 per double pair only
  });

  // Merge items with the same SKU into one line (qtys summed, first occurrence's
  // other fields kept). Sku-less lines (undefined sku or '—') are appended last.
  const skuMap = new Map();
  const noSkuItems = [];
  items.forEach(item => {
    if (!item.sku || item.sku === '—') { noSkuItems.push(item); return; }
    if (skuMap.has(item.sku)) {
      skuMap.get(item.sku).qty += item.qty;
    } else {
      skuMap.set(item.sku, { ...item });
    }
  });

  return { items: [...skuMap.values(), ...noSkuItems], warnings };
}
